import * as echarts from 'echarts';
import { debounce } from 'lodash-es';
import { inject, nextTick, onBeforeUnmount, onMounted, type Ref, watch } from 'vue';
import { Logger, THEME_STORE } from '../../utils';

type Opts = echarts.EChartsInitOpts & {
  debug?: boolean;
};

export const useECharts = (
  dom: Ref<HTMLDivElement> | Ref<HTMLCanvasElement> | Ref<null>,
  theme?: string,
  opts?: Opts,
): {
  getInstance: () => echarts.ECharts | null;
  setOptions: (options: echarts.EChartsOption) => void;
  resize: (opts?: echarts.ResizeOpts) => void;
  clear: () => void;
} => {
  let log: Logger | null = null;
  // 初始化
  if (opts?.debug) {
    log = new Logger('useECharts');
  }

  // 获取系统主题
  const systemTheme = inject(THEME_STORE);
  log?.info('systemTheme', systemTheme);

  let myChart: echarts.ECharts | null = null;
  let cacheOptions: echarts.EChartsOption | null = null;

  onMounted(async () => {
    await nextTick();
    await initChart();
    window.addEventListener('resize', debounce(resize, 200));
  });

  onBeforeUnmount(() => {
    clear();
    window.removeEventListener('resize', debounce(resize, 200));
  });

  watch(
    () => systemTheme?.value,
    () => {
      if (!theme) {
        clear();
        initChart();
      }
    },
  );

  async function initChart() {
    log?.info('initChart');
    await nextTick();
    const options = opts || undefined;
    if (options) {
      delete options.debug;
    }
    myChart = echarts.init(dom.value, theme || systemTheme?.value || 'light', options);
    if (cacheOptions) {
      setOptions(cacheOptions);
    }
  }

  function setOptions(option: echarts.EChartsOption, notMerge?: boolean, lazyUpdate?: boolean) {
    log?.info('setOptions', option, notMerge, lazyUpdate);
    cacheOptions = option;
    myChart?.setOption(
      Object.assign(option, {
        backgroundColor: 'transparent',
      }),
      notMerge,
      lazyUpdate,
    );
  }

  function clear() {
    if (myChart) {
      log?.info('clear');
      myChart.dispose();
      myChart = null;
    }
  }
  function resize() {
    if (myChart) {
      log?.info('resize');
      myChart.resize();
    }
  }

  function getInstance() {
    return myChart;
  }

  return {
    getInstance,
    setOptions,
    resize,
    clear,
  };
};
